import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import PostCard from "../components/PostCard";
import CreatePostDialog from "../components/CreatePostDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import {
    MagnifyingGlass, AirplaneTilt, Buildings, MapPin, Camera, Sparkle, Funnel, Compass,
} from "@phosphor-icons/react";

const CATEGORY_FILTERS = [
    { v: "", label: "All", Icon: Sparkle },
    { v: "destination", label: "Destinations", Icon: MapPin },
    { v: "flight", label: "Flights", Icon: AirplaneTilt },
    { v: "hotel", label: "Hotels", Icon: Buildings },
    { v: "activity", label: "Activities", Icon: Camera },
];

const TRAVEL_ASSET = "https://static.prod-images.emergentagent.com/jobs/7bfd5dde-59f4-4f41-bd29-ac397f2276b1/images/d38fcf11f4778de2d9ac59af0a8d1ac2a339878b84a6716651a0c6e4e5498e38.png";

export default function FeedPage() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [trending, setTrending] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [city, setCity] = useState("");
    const [cat, setCat] = useState("");
    const [sort, setSort] = useState("new");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (q) params.q = q;
            if (city) params.city = city;
            if (cat) params.category = cat;
            if (sort) params.sort = sort;
            const { data } = await api.get("/posts", { params });
            setPosts(data);
        } finally {
            setLoading(false);
        }
    }, [q, city, cat, sort]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        (async () => {
            try {
                const [t, s] = await Promise.all([
                    api.get("/posts/trending"),
                    api.get("/stats"),
                ]);
                setTrending(t.data);
                setStats(s.data);
            } catch (err) {
                // non-fatal: sidebar widgets stay empty
                console.error("trending/stats fetch failed:", err);
            }
        })();
    }, [posts.length]);

    const onUpdate = (updated) => {
        setPosts((cur) => cur.map((p) => (p.id === updated.id ? updated : p)));
        setTrending((cur) => cur.map((p) => (p.id === updated.id ? updated : p)));
    };

    const initials = user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "T";

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="feed-page">
            <Navbar />

            {/* Banner */}
            <header className="bg-gradient-to-br from-[#990000] to-[#7A0000] relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-72 h-72 rounded-full bg-[#FFCC00]/10 blur-3xl" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 relative">
                    <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
                        <div>
                            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFCC00] mb-2">The Feed</div>
                            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter">
                                What Trojans say about<br /> traveling India
                            </h1>
                        </div>
                        <CreatePostDialog onCreated={() => load()} />
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Search bar */}
                <div className="bg-white border border-stone-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-3">
                    <div className="flex-1 flex items-center gap-2 border border-stone-200 rounded-lg px-3 h-11">
                        <MagnifyingGlass size={18} className="text-stone-400" />
                        <input
                            data-testid="feed-search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search title, description, tags…"
                            className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400"
                        />
                    </div>
                    <Input
                        data-testid="feed-city-input"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Filter by city"
                        className="md:w-48 h-11 border-stone-200 focus-visible:ring-[#990000]"
                    />
                    <Select value={sort} onValueChange={setSort}>
                        <SelectTrigger data-testid="feed-sort-select" className="md:w-36 h-11 border-stone-200">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="new" data-testid="sort-new-option">Newest</SelectItem>
                            <SelectItem value="top" data-testid="sort-top-option">Top voted</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left: filters */}
                    <aside className="lg:col-span-3">
                        <div className="bg-white border border-stone-200 rounded-xl p-5 sticky top-20">
                            <div className="flex items-center gap-2 mb-4">
                                <Funnel size={16} className="text-[#990000]" />
                                <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600">Categories</div>
                            </div>
                            <div className="space-y-1">
                                {CATEGORY_FILTERS.map((c) => {
                                    const active = cat === c.v;
                                    return (
                                        <button
                                            key={c.label}
                                            data-testid={`filter-cat-${c.v || "all"}`}
                                            onClick={() => setCat(c.v)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                                active
                                                    ? "bg-[#990000] text-white"
                                                    : "text-stone-700 hover:bg-stone-50"
                                            }`}
                                        >
                                            <c.Icon size={16} weight={active ? "fill" : "regular"} />
                                            {c.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {stats && (
                                <div className="mt-6 pt-6 border-t border-stone-100">
                                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-500 mb-3">Community</div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-stone-50 rounded-lg p-3">
                                            <div className="font-heading text-xl font-bold text-[#0A0A0A]">{stats.total_posts}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Posts</div>
                                        </div>
                                        <div className="bg-stone-50 rounded-lg p-3">
                                            <div className="font-heading text-xl font-bold text-[#0A0A0A]">{stats.total_users}</div>
                                            <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Trojans</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* Center: posts */}
                    <section className="lg:col-span-6 space-y-4">
                        {loading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <div key={`feed-skeleton-${i}`} className="bg-white border border-stone-200 rounded-xl p-6 animate-pulse">
                                    <div className="h-3 w-24 bg-stone-200 rounded mb-3" />
                                    <div className="h-5 w-3/4 bg-stone-200 rounded mb-3" />
                                    <div className="h-3 w-full bg-stone-100 rounded mb-2" />
                                    <div className="h-3 w-5/6 bg-stone-100 rounded" />
                                </div>
                            ))
                        ) : posts.length === 0 ? (
                            <div className="bg-white border border-stone-200 rounded-xl p-12 text-center">
                                <img src={TRAVEL_ASSET} alt="" className="w-32 h-32 mx-auto mb-6 opacity-90" />
                                <div className="font-heading text-xl font-semibold text-[#0A0A0A] mb-2">No posts yet</div>
                                <p className="text-stone-500 mb-5 max-w-sm mx-auto text-sm">
                                    Be the first Trojan to share a travel tip in this category. Your alma mater family is waiting!
                                </p>
                                <CreatePostDialog onCreated={() => load()} />
                            </div>
                        ) : (
                            posts.map((p, i) => (
                                <PostCard key={p.id} post={p} onUpdate={onUpdate} index={i} />
                            ))
                        )}
                    </section>

                    {/* Right: trending */}
                    <aside className="lg:col-span-3">
                        <div className="bg-white border border-stone-200 rounded-xl p-5 sticky top-20">
                            <div className="flex items-center gap-2 mb-4">
                                <Compass size={16} className="text-[#990000]" />
                                <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600">Trending</div>
                            </div>
                            {trending.length === 0 ? (
                                <div className="text-xs text-stone-400">Nothing trending yet</div>
                            ) : (
                                <ol className="space-y-3">
                                    {trending.slice(0, 5).map((p, i) => (
                                        <li key={p.id}>
                                            <Link
                                                to={`/posts/${p.id}`}
                                                data-testid={`trending-link-${p.id}`}
                                                className="flex gap-3 group"
                                            >
                                                <div className="font-heading text-xl font-bold text-stone-300 group-hover:text-[#990000] w-5 flex-shrink-0">
                                                    {i + 1}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-medium text-[#0A0A0A] group-hover:text-[#990000] line-clamp-2 leading-snug">
                                                        {p.title}
                                                    </div>
                                                    <div className="text-xs text-stone-400 mt-1">
                                                        {p.city} · {p.upvotes} upvotes
                                                    </div>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ol>
                            )}
                            <div className="mt-6 pt-6 border-t border-stone-100">
                                <div className="bg-gradient-to-br from-[#FFF4CC] to-[#FFE680] rounded-lg p-4 border border-[#FFCC00]/40">
                                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#5C4400] mb-1">Hi {user?.name?.split(" ")[0]} 👋</div>
                                    <div className="font-heading text-base font-semibold text-[#5C0000] leading-tight mb-2">
                                        Got a tip from your last trip?
                                    </div>
                                    <CreatePostDialog
                                        onCreated={() => load()}
                                        trigger={
                                            <Button data-testid="sidebar-share-btn" className="bg-[#990000] hover:bg-[#7A0000] text-white w-full h-9 text-xs">
                                                Share with Trojans
                                            </Button>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>
        </div>
    );
}
